import { useEffect, useMemo, useState } from "react";
import { findRegimen, regimenBadge, type Regimen } from "@/data/meal-plan";
import type { DayOfWeek, DayPlan, WeekPlan } from "@/types/meal-plan-state";
import { DAYS_OF_WEEK } from "@/types/meal-plan-state";
import { fetchWeekSnapshot, type WeekSummary } from "@/lib/api";
import { ownerScope } from "@/lib/week-owner";
import { useWeekPlan } from "@/context/week-plan-context";
import { migrateWeekPlan } from "@/lib/migrate-plan";
import { getMealChoices, getMealMenu } from "@/lib/random-plan";
import { formatWeekRange } from "@/lib/week-utils";
import { WeekCalendarPicker } from "./week-calendar-picker";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { hasEatenMarks, withEatenLossWarning } from "@/lib/owner-eaten";

interface DuplicateWeekDialogProps {
  currentWeekId: string;
  currentWeekPlan: WeekPlan;
  weekList: WeekSummary[];
  onDuplicate: (selections: Record<DayOfWeek, { sourcePlan: DayPlan; mealIndices: number[] } | null>) => void;
  onClose: () => void;
}

/** Check if a day has any actual selections */
function hasSelections(plan: DayPlan): boolean {
  if (!plan.meals) return false;
  return plan.meals.some((meal) =>
    meal.some((cg) => cg.selections.length > 0 || cg.skipped),
  );
}

/** Get meal items as display strings */
function getMealItems(dayPlan: DayPlan, regimen: Regimen, mealIndex: number): string[] {
  const meal = regimen.meals[mealIndex]!;
  const mealSels = dayPlan.meals?.[mealIndex];
  if (!mealSels) return [];

  const menuId = dayPlan.mealMenus?.[mealIndex];
  const choices = getMealChoices(meal, menuId);
  const menu = getMealMenu(meal, menuId);
  const items: string[] = menu ? [menu.name] : [];
  for (let ci = 0; ci < choices.length; ci++) {
    const group = choices[ci]!;
    const cgSels = mealSels[ci];
    if (!cgSels || cgSels.skipped) continue;
    for (const sel of cgSels.selections) {
      const opt = group.options.find((o) => o.id === sel.optionId);
      if (!opt) continue;
      items.push(sel.count > 1 ? `${opt.name} x${sel.count}` : opt.name);
    }
  }
  return items;
}

export function DuplicateWeekDialog({
  currentWeekId,
  currentWeekPlan,
  weekList,
  onDuplicate,
  onClose,
}: DuplicateWeekDialogProps) {
  // Sources are the week owner's own weeks (a friend's in friend mode).
  const { owner } = useWeekPlan();
  const scope = useMemo(() => ownerScope(owner), [owner]);
  const [sourceWeekId, setSourceWeekId] = useState<string | null>(null);
  const [sourceWeekPlan, setSourceWeekPlan] = useState<WeekPlan | null>(null);
  const [loading, setLoading] = useState(false);
  // Per-day meal selections: which meals to copy for each day
  const [dayMealSelections, setDayMealSelections] = useState<Record<string, Set<number>>>({});
  const [showConfirm, setShowConfirm] = useState(false);

  // Fetch source week plan when selected
  useEffect(() => {
    if (!sourceWeekId || sourceWeekId === currentWeekId) {
      setSourceWeekPlan(null);
      setDayMealSelections({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    setDayMealSelections({});
    fetchWeekSnapshot(scope, sourceWeekId)
      .then((snapshot) => snapshot.plan)
      .then((plan) => {
        if (!cancelled && plan) {
          const { plan: migrated } = migrateWeekPlan(plan);
          setSourceWeekPlan(migrated);
          setLoading(false);

          // Auto-select all meals for matching days
          const selections: Record<string, Set<number>> = {};
          for (const { id } of DAYS_OF_WEEK) {
            const sourceDayPlan = migrated[id];
            const targetDayPlan = currentWeekPlan[id];

            if (
              sourceDayPlan?.regimenId &&
              sourceDayPlan.regimenId === targetDayPlan?.regimenId &&
              hasSelections(sourceDayPlan)
            ) {
              const regimen = findRegimen(sourceDayPlan.regimenId);
              if (regimen) {
                const indices = new Set<number>();
                regimen.meals.forEach((_, mi) => {
                  const mealSels = sourceDayPlan.meals?.[mi];
                  if (mealSels && mealSels.some((cg) => cg.selections.length > 0 || cg.skipped)) {
                    indices.add(mi);
                  }
                });
                if (indices.size > 0) {
                  selections[id] = indices;
                }
              }
            }
          }
          if (!cancelled) {
            setDayMealSelections(selections);
          }
        } else if (!cancelled) {
          setSourceWeekPlan(null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSourceWeekPlan(null);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [sourceWeekId, scope]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  function toggleMeal(day: DayOfWeek, mealIndex: number) {
    setDayMealSelections((prev) => {
      const daySet = new Set(prev[day] ?? []);
      if (daySet.has(mealIndex)) {
        daySet.delete(mealIndex);
      } else {
        daySet.add(mealIndex);
      }
      const next = { ...prev };
      if (daySet.size > 0) {
        next[day] = daySet;
      } else {
        delete next[day];
      }
      return next;
    });
  }

  function toggleDay(day: DayOfWeek, regimen: Regimen, dayPlan: DayPlan) {
    setDayMealSelections((prev) => {
      const daySet = prev[day];
      const next = { ...prev };

      if (daySet && daySet.size > 0) {
        // Deselect all
        delete next[day];
      } else {
        // Select all meals with content
        const indices = new Set<number>();
        regimen.meals.forEach((_, mi) => {
          const mealSels = dayPlan.meals?.[mi];
          if (mealSels && mealSels.some((cg) => cg.selections.length > 0 || cg.skipped)) {
            indices.add(mi);
          }
        });
        if (indices.size > 0) {
          next[day] = indices;
        }
      }
      return next;
    });
  }

  // Count total selected meals
  const totalSelectedMeals = Object.values(dayMealSelections).reduce(
    (sum, set) => sum + set.size, 0,
  );

  function handleDuplicate() {
    if (!sourceWeekPlan || totalSelectedMeals === 0) return;
    setShowConfirm(true);
  }

  function confirmDuplicate() {
    if (!sourceWeekPlan) return;
    const selections: Record<string, { sourcePlan: DayPlan; mealIndices: number[] } | null> = {};
    for (const { id } of DAYS_OF_WEEK) {
      const mealSet = dayMealSelections[id];
      if (mealSet && mealSet.size > 0) {
        selections[id] = {
          sourcePlan: sourceWeekPlan[id],
          mealIndices: Array.from(mealSet),
        };
      } else {
        selections[id] = null;
      }
    }
    onDuplicate(selections as Record<DayOfWeek, { sourcePlan: DayPlan; mealIndices: number[] } | null>);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-background rounded-t-2xl sm:rounded-2xl w-full max-w-md mx-auto p-5 space-y-4 safe-area-bottom max-h-[85dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="font-semibold text-base">Duplicar semana</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Elige la semana de la que quieres copiar y selecciona las comidas
          </p>
        </div>

        {/* Week calendar picker */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Semana origen</label>
          <WeekCalendarPicker
            weekList={weekList}
            selectedWeekId={sourceWeekId}
            targetWeekId={currentWeekId}
            onSelectWeek={(weekId) => {
              if (weekId === currentWeekId) return; // Can't duplicate from same week
              setSourceWeekId(weekId);
            }}
          />
          {sourceWeekId && (
            <p className="text-xs text-primary font-medium mt-1">
              {formatWeekRange(sourceWeekId)}
            </p>
          )}
        </div>

        {/* Per-day meal selection */}
        {sourceWeekId && loading && <p className="text-sm text-muted-foreground">Cargando...</p>}
        {sourceWeekId && !loading && !sourceWeekPlan && (
          <p className="text-sm text-muted-foreground">No se encontró la semana</p>
        )}

        {sourceWeekPlan && !loading && (
          <div className="space-y-2">
            {DAYS_OF_WEEK.map(({ id, label }) => {
              const sourceDayPlan = sourceWeekPlan[id];
              const targetDayPlan = currentWeekPlan[id];
              const sameRegimen =
                sourceDayPlan?.regimenId &&
                sourceDayPlan.regimenId === targetDayPlan?.regimenId;
              const noData = !sourceDayPlan?.regimenId || !hasSelections(sourceDayPlan);
              const regimen = findRegimen(sourceDayPlan?.regimenId);

              const disabled = !sameRegimen || noData;
              const daySelected = dayMealSelections[id];

              if (disabled) {
                return (
                  <div key={id} className="border rounded-lg p-2.5 opacity-40">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold w-8">{label}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {!sourceDayPlan?.regimenId
                          ? "Sin régimen"
                          : !sameRegimen
                            ? "Régimen diferente"
                            : "Sin selecciones"}
                      </span>
                    </div>
                  </div>
                );
              }

              return (
                <div key={id} className={`border rounded-lg p-2.5 space-y-1 ${daySelected && daySelected.size > 0 ? "border-primary/40 bg-primary/5" : ""}`}>
                  <button
                    onClick={() => toggleDay(id, regimen!, sourceDayPlan)}
                    className="flex items-center gap-2 w-full text-left"
                  >
                    <input
                      type="checkbox"
                      checked={!!daySelected && daySelected.size > 0}
                      readOnly
                      className="accent-primary"
                    />
                    <span className="text-xs font-semibold">{label}</span>
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground ml-auto">
                      {regimenBadge(sourceDayPlan.regimenId!)}
                    </span>
                  </button>

                  {/* Individual meal checkboxes */}
                  <div className="ml-5 space-y-0.5">
                    {regimen!.meals.map((meal, mi) => {
                      const items = getMealItems(sourceDayPlan, regimen!, mi);
                      const mealSels = sourceDayPlan.meals?.[mi];
                      const hasContent = items.length > 0 || (mealSels?.some((cg) => cg.skipped) ?? false);
                      const isChecked = daySelected?.has(mi) ?? false;
                      const note = sourceDayPlan.mealNotes?.[mi];

                      return (
                        <label
                          key={mi}
                          className={`flex items-start gap-1.5 rounded p-1 cursor-pointer text-xs ${!hasContent ? "opacity-40" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleMeal(id, mi)}
                            disabled={!hasContent}
                            className="mt-0.5 accent-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">
                              {meal.label}
                              {note && <span className="font-normal text-muted-foreground italic ml-1">({note})</span>}
                            </span>
                            {items.length > 0 && (
                              <p className="text-muted-foreground truncate">{items.join(", ")}</p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={handleDuplicate}
            disabled={totalSelectedMeals === 0}
            className="flex-1 text-sm font-medium py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Duplicar semana {totalSelectedMeals > 0 ? `(${totalSelectedMeals})` : ""}
          </button>
          <button
            onClick={onClose}
            className="text-sm font-medium py-2 px-4 rounded-md border hover:bg-muted transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
        title="¿Reemplazar las comidas seleccionadas?"
        description={withEatenLossWarning(
          "Las comidas seleccionadas de la semana actual se reemplazarán con las de la semana origen.",
          owner,
          DAYS_OF_WEEK.some(({ id }) => hasEatenMarks(currentWeekPlan[id].meals, dayMealSelections[id] ?? [])),
        )}
        confirmText="Reemplazar"
        destructive
        onConfirm={confirmDuplicate}
      />
    </div>
  );
}
